/* Preview wording per FR59. Kept out of the row so the photo, video and voice cases, which
 * all read the same media body, cannot drift apart. */

import { i18n } from '../../localization/i18n';
import { MISSED_LIKE } from '../../Helpers/Constants';
import {
    GROUP_CREATED,
    GROUP_IMAGE_ADDED,
    GROUP_IMAGE_UPDATED,
    GROUP_TITLE_UPDATED,
    GROUP_USER_ADDED,
    GROUP_USER_LEFT,
    GROUP_USER_MADE_ADMIN,
    GROUP_USER_REMOVED,
    POLL_MESSAGE_TYPE,
} from '../../Helpers/Chat/Constant';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const MILLIS_PER_SECOND = 1000;

// Read on call, not at module load: i18n resolves the active language at render time.
const label = (key, params) => i18n.t(`chat.preview.${key}`, params);

/**
 * Formats a duration per common rule 18.
 *
 * Kept apart from millisToMinutesAndSeconds, whose mm:ss the media and reply
 * previews are specified in.
 *
 * @param millis Duration in milliseconds.
 * @returns Formatted duration, or an empty string when the value is unusable.
 */
export const formatDuration = millis => {
    const totalSeconds = Math.floor(Number(millis) / MILLIS_PER_SECOND);
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';

    if (totalSeconds < SECONDS_PER_MINUTE) return `${totalSeconds}s`;

    if (totalSeconds < SECONDS_PER_HOUR) {
        const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
        return `${minutes}m ${totalSeconds % SECONDS_PER_MINUTE}s`;
    }

    const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
    return `${hours}h ${minutes}m`;
};

/**
 * Reads how many photos and videos left in the same send as this message.
 *
 * Every file of one selection carries the same batchId, so any single message of
 * the batch knows the whole count.
 *
 * @param media Media body of the message.
 * @returns Counts, both 0 for a message sent on its own.
 */
export const getBatchCounts = (media = {}) => {
    if (!media.batchId) return { imageCount: 0, videoCount: 0 };

    const imageCount = Number(media.batchImages) || 0;
    const videoCount = Number(media.batchVideos) || 0;
    return { imageCount, videoCount };
};

// A batch is labelled by its counts per FR59, so only a lone photo falls back here.
export const getPhotoPreview = (media = {}) => {
    const { imageCount, videoCount } = getBatchCounts(media);

    if (imageCount > 0 && videoCount > 0) {
        return label('PHOTOS_VIDEOS', { photos: imageCount, videos: videoCount });
    }
    if (imageCount > 1) return label('PHOTOS', { count: imageCount });
    return label('PHOTO');
};

export const getVideoPreview = (media = {}) => {
    const { imageCount, videoCount } = getBatchCounts(media);

    if (imageCount > 0 && videoCount > 0) {
        return label('PHOTOS_VIDEOS', { photos: imageCount, videos: videoCount });
    }
    if (videoCount > 1) return label('VIDEOS', { count: videoCount });
    return label('VIDEO');
};

// Only a recording is a "voice message"; an uploaded file keeps its name like a document.
// Older recordings carry no duration, so the suffix is dropped rather than shown as "0:00".
export const getAudioPreview = (media = {}) => {
    if (media.audioType !== 'recording') {
        return media.fileName || label('VOICE_MESSAGE');
    }

    const duration = formatDuration(media.duration);
    if (!duration) return label('VOICE_MESSAGE');

    return `${label('VOICE_MESSAGE')} · ${duration}`;
};

export const getDocumentPreview = (media = {}) => media.fileName || label('DOCUMENT');

/**
 * "Contact: {name}" (FR119); a card with no name, from outside Dazway, shows its number.
 *
 * @param contact `msgBody.contact` of the message.
 * @returns Preview text.
 */
export const getContactPreview = (contact = {}) => {
    const numbers = [].concat(contact.phone_number || []);
    const name = contact.name || numbers[0];
    return name ? label('CONTACT_NAME', { name }) : label('CONTACT');
};

/**
 * Tells whether a location message is being shared live.
 *
 * Sharing live is a mobile feature; the web only reports it. The flag travels in
 * meta_data, which rides beside the body rather than inside it, because the recent
 * chat path keeps only latitude and longitude of the body. The body is read as well
 * so the preview still holds if the flag ever moves in there.
 *
 * @param msgBody Message body holding the location.
 * @param metaData Message meta_data, already parsed.
 * @returns Whether the location is live.
 */
export const isLiveLocation = (msgBody = {}, metaData = {}) =>
    String(metaData?.isLive).toLowerCase() === 'true' || Boolean(msgBody?.location?.isLive);

export const getLocationPreview = (location = {}, isLive = false) =>
    isLive || location.isLive ? label('LIVE_LOCATION') : label('LOCATION');

// Not the bubble's builder: FR71 names the outcome ("You missed a voice call", a
// duration) where FR59 names the state. Two specs, two wordings.
const CALL_STATE_IN_CALL = 'IN_CALL';
const CALL_STATE_RINGING = 'RINGING';
const CALL_STATE_MISSED = 'MISSED';
const CALL_STATE_ENDED = 'ENDED';

const CALL_STATE_TONE = {
    [CALL_STATE_IN_CALL]: 'answered',
    [CALL_STATE_RINGING]: 'answered',
    [CALL_STATE_MISSED]: 'missed',
    [CALL_STATE_ENDED]: 'ended',
};

const RINGING_STATUSES = ['incoming', 'outgoing', 'ringing', 'calling'];
const IN_CALL_STATUSES = ['connected', 'ongoing', 'attended'];
// A refused call reads as missed here: FR59 has no state of its own for it.
const REFUSED_STATUSES = ['declined', 'rejected'];

/**
 * Preview wording and colour for a call, per FR59.
 *
 * @param status Call status as the SDK reports it.
 * @param isVideo Whether the call carries video.
 * @param viewerIsCaller Whether the viewer placed the call.
 * @param viewerMissed Whether the viewer is listed as having missed or refused it.
 * @returns The preview text and the tone class suffix driving its colour.
 */
export const getCallPreview = ({ status = '', isVideo = false, viewerIsCaller = false, viewerMissed = false } = {}) => {
    const normalised = String(status).toLowerCase();
    // An unanswered call reads as missed for the callee and as ended for the caller.
    const missed =
        viewerMissed || REFUSED_STATUSES.includes(normalised) || (MISSED_LIKE.includes(normalised) && !viewerIsCaller);

    let state = CALL_STATE_ENDED;
    if (IN_CALL_STATUSES.includes(normalised)) {
        state = CALL_STATE_IN_CALL;
    } else if (RINGING_STATUSES.includes(normalised)) {
        state = CALL_STATE_RINGING;
    } else if (missed) {
        state = CALL_STATE_MISSED;
    }

    const type = label(isVideo ? 'VIDEO_CALL' : 'VOICE_CALL');

    return { text: `${type} · ${label(state)}`, tone: CALL_STATE_TONE[state] };
};

// Not groupstatus(): the conversation status line reads the same events through it, and
// FR73 has yet to define that wording.
// TODO: Fold this back into groupstatus() once FR73 defines the status line wording.
const SYSTEM_EVENT_LABELS = {
    [GROUP_CREATED]: ['EVENT_GROUP_CREATED_YOU', 'EVENT_GROUP_CREATED'],
    [GROUP_USER_ADDED]: ['EVENT_ADDED_YOU', 'EVENT_ADDED'],
    [GROUP_USER_REMOVED]: ['EVENT_REMOVED_YOU', 'EVENT_REMOVED'],
    [GROUP_USER_LEFT]: ['EVENT_LEFT_YOU', 'EVENT_LEFT'],
    [GROUP_TITLE_UPDATED]: ['EVENT_RENAMED_YOU', 'EVENT_RENAMED'],
    [GROUP_IMAGE_ADDED]: ['EVENT_PHOTO_YOU', 'EVENT_PHOTO'],
    [GROUP_IMAGE_UPDATED]: ['EVENT_PHOTO_YOU', 'EVENT_PHOTO'],
    [GROUP_USER_MADE_ADMIN]: ['EVENT_ADMIN_YOU', 'EVENT_ADMIN'],
};

// An event about the viewer is worded in the second person; the actor is named otherwise.
const SUBJECT_IS_TARGET = [GROUP_USER_ADDED, GROUP_USER_REMOVED, GROUP_USER_MADE_ADMIN];

/**
 * Preview wording for a group system event, per FR59.
 *
 * @param status Event status as the SDK reports it in profileUpdatedStatus.
 * @param actorName Display name of the participant who acted.
 * @param targetName Display name of the participant acted upon.
 * @param isActorMe Whether the viewer is the participant who acted.
 * @param isTargetMe Whether the viewer is the participant acted upon.
 * @param groupName Current name of the group, for a rename event.
 * @returns The event wording, or an empty string for an event FR59 does not cover.
 */
export const getSystemEventPreview = ({
    status,
    actorName = '',
    targetName = '',
    isActorMe = false,
    isTargetMe = false,
    groupName = '',
} = {}) => {
    const keys = SYSTEM_EVENT_LABELS[status];
    if (!keys) return '';

    const aboutViewer = SUBJECT_IS_TARGET.includes(status) ? isTargetMe : isActorMe;
    const [selfKey, otherKey] = keys;

    // "[Name] added you" names the actor; the other second-person events name nobody.
    if (aboutViewer) {
        return label(selfKey, { name: actorName, title: groupName });
    }

    const name = SUBJECT_IS_TARGET.includes(status) ? targetName : actorName;
    return label(otherKey, { name, title: groupName });
};

/**
 * Preview wording for a media message, as plain text.
 *
 * The row renders media previews as an icon beside the text, but a reaction summary quotes
 * the message inside a sentence and so needs the wording on its own.
 *
 * @param msgType Message type of the reacted message.
 * @param msgBody Message body holding the media, contact or location.
 * @returns The preview wording, or an empty string for a type that carries none.
 */
export const getMediaPreviewText = (msgType, msgBody = {}) => {
    switch (msgType) {
        case 'image':
            return getPhotoPreview(msgBody.media);
        case 'video':
            return getVideoPreview(msgBody.media);
        case 'audio':
            return getAudioPreview(msgBody.media);
        case 'file':
            return getDocumentPreview(msgBody.media);
        case 'contact':
            return getContactPreview(msgBody.contact);
        case 'location':
            return getLocationPreview(msgBody.location);
        default:
            return '';
    }
};

const TEXT_MESSAGE_TYPES = ['text', 'auto_text'];

/**
 * Plain wording of a reacted message, quoted inside a reaction summary.
 *
 * @param msgType Message type known to the caller; the body's own type wins.
 * @param msgBody Body of the reacted message.
 * @returns One line of text, empty when the message carries none.
 */
export const getReactionTargetText = (msgType, msgBody = {}) => {
    const type = msgBody?.message_type || msgType;
    if (TEXT_MESSAGE_TYPES.includes(type)) return String(msgBody?.message || '').replace(/\s+/g, ' ');
    if (type === POLL_MESSAGE_TYPE) return msgBody?.poll?.question || '';
    return getMediaPreviewText(type, msgBody || {});
};

/**
 * Reaction summary shared by the chat list and the desktop notification (FR93 Q9).
 *
 * @param isOwn Whether the viewer is the reactor.
 * @param isGroup Whether the chat is a group, where the reactor is named.
 * @param reactorName Display name of the reactor.
 * @param emojiChar The emoji reacted with.
 * @param targetText Wording of the reacted message, from getReactionTargetText.
 * @returns The summary.
 */
export const getReactionPreviewText = ({
    isOwn = false,
    isGroup = false,
    reactorName = '',
    emojiChar = '',
    targetText = '',
} = {}) => {
    const params = { name: reactorName, emoji: emojiChar, message: targetText };
    if (isOwn) return label('YOU_REACTED', params);
    return label(isGroup ? 'NAME_REACTED' : 'REACTED', params);
};
